const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, _reject) => {
    if (!src) {
      // Return a 1x1 transparent image if source is empty
      const emptyImg = new Image();
      emptyImg.width = 1;
      emptyImg.height = 1;
      resolve(emptyImg);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    // Set onload before setting src to ensure it catches
    img.onload = () => resolve(img);
    
    img.onerror = (err) => {
      console.error(`Error loading image from: ${src}`, err);
      // Try adding a cache-busting parameter
      if (!src.includes('?')) {
        const newSrc = `${src}?cachebust=${new Date().getTime()}`;
        console.log(`Retrying with: ${newSrc}`);
        const retryImg = new Image();
        retryImg.crossOrigin = 'anonymous';
        retryImg.onload = () => resolve(retryImg);
        retryImg.onerror = () => {
          console.error(`Failed retry loading image: ${newSrc}`);
          // Return a 1x1 transparent image as fallback
          const fallbackImg = new Image();
          fallbackImg.width = 1;
          fallbackImg.height = 1;
          resolve(fallbackImg);
        };
        retryImg.src = newSrc;
      } else {
        // Return a 1x1 transparent image as fallback
        const fallbackImg = new Image();
        fallbackImg.width = 1;
        fallbackImg.height = 1;
        resolve(fallbackImg);
      }
    };
    
    // Set src after setting event handlers
    img.src = src;
  });
};

// export const mergeImages = async (shapeUrl: string, flavorUrl: string, color: string, toppingUrl: string): Promise<string> => {
//   const [shapeImg, flavorImg, toppingImg] = await Promise.all([
//     loadImage(shapeUrl),
//     loadImage(flavorUrl),
//     loadImage(toppingUrl),
//   ]);

//   const canvas = document.createElement('canvas');
//   const ctx = canvas.getContext('2d');

//   if (!ctx) {
//     throw new Error("Canvas rendering context is not available");
//   }

//   const width = Math.max(shapeImg.width, flavorImg.width, toppingImg.width);
//   const height = Math.max(shapeImg.height, flavorImg.height, toppingImg.height);

//   canvas.width = width;
//   canvas.height = height;

//   ctx.drawImage(shapeImg, 0, 0, width, height);
//   ctx.drawImage(flavorImg, 0, 0, width, height);
//   ctx.drawImage(toppingImg, 0, 0, width, height);


//   return canvas.toDataURL();
//   -----------------
//   ctx.drawImage(shapeImg, 0, 0, width, height);
//   ctx.drawImage(flavorImg, 0, 0, width, height);

//   // Fill the canvas with the specified color
//   ctx.globalCompositeOperation = 'source-atop';
//   ctx.fillStyle = color;
//   ctx.fillRect(0, 0, width, height);
//   ctx.globalCompositeOperation = 'source-over';

//   ctx.drawImage(toppingImg, 0, 0, width, height);

//   return canvas.toDataURL();
// };

export const mergeImages = async (
  shapeUrl: string,
  flavorUrl: string,
  color: string,
  toppingUrl: string | undefined,
  uniqueColorImg: string
): Promise<string> => {
  try {
    console.log("Starting image merge with:", {
      shapeUrl,
      flavorUrl,
      color,
      toppingUrl,
      uniqueColorImg
    });

    // Load images with better error handling
    let shapeImg, flavorImg, uniqueColorImage;
    try {
      shapeImg = await loadImage(shapeUrl);
    } catch (err) {
      console.error("Failed to load shape image:", err);
      return ''; // Return empty if we can't even load the shape
    }

    try {
      flavorImg = await loadImage(flavorUrl);
    } catch (err) {
      console.warn("Flavor image failed to load, proceeding with shape only");
      flavorImg = new Image(1, 1); // Use a tiny image as placeholder
    }

    try {
      uniqueColorImage = await loadImage(uniqueColorImg);
    } catch (err) {
      console.warn("Color image failed to load, proceeding without it");
      uniqueColorImage = new Image(1, 1); // Use a tiny image as placeholder
    }

    // Optionally load topping image
    let toppingImg: HTMLImageElement | null = null;
    if (toppingUrl) {
      try {
        toppingImg = await loadImage(toppingUrl);
      } catch (err) {
        console.warn("Topping image failed to load, proceeding without it.");
      }
    }

    // Merge shape and flavor images
    const tempCanvas1 = document.createElement('canvas');
    const tempCtx1 = tempCanvas1.getContext('2d');
    if (!tempCtx1) {
      throw new Error("Temporary canvas rendering context 1 is not available");
    }

    const width1 = Math.max(shapeImg.width, flavorImg.width);
    const height1 = Math.max(shapeImg.height, flavorImg.height);
    tempCanvas1.width = width1;
    tempCanvas1.height = height1;
    tempCtx1.drawImage(shapeImg, 0, 0, width1, height1);
    if (flavorImg.width > 1 && flavorImg.height > 1) {
      tempCtx1.drawImage(flavorImg, 0, 0, width1, height1);
    }

    // Apply color to unique color image (only if color is provided)
    let tempCanvas2: HTMLCanvasElement | null = null;
    let width2 = 0;
    let height2 = 0;
    
    // Only apply color layer if color is provided and not empty
    if (color && color.trim() !== '') {
      tempCanvas2 = document.createElement('canvas');
    const tempCtx2 = tempCanvas2.getContext('2d');
    if (!tempCtx2) {
      throw new Error("Temporary canvas rendering context 2 is not available");
    }

      width2 = uniqueColorImage.width;
      height2 = uniqueColorImage.height;
    tempCanvas2.width = width2;
    tempCanvas2.height = height2;
    if (uniqueColorImage.width > 1 && uniqueColorImage.height > 1) {
      tempCtx2.drawImage(uniqueColorImage, 0, 0, width2, height2);
      tempCtx2.globalCompositeOperation = 'source-atop';
        tempCtx2.fillStyle = color;
      tempCtx2.fillRect(0, 0, width2, height2);
      tempCtx2.globalCompositeOperation = 'destination-in';
      }
    }

    // Final merge with topping image
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    if (!finalCtx) {
      throw new Error("Final canvas rendering context is not available");
    }

    const finalWidth = Math.max(width1, width2, toppingImg ? toppingImg.width : 0);
    const finalHeight = Math.max(height1, height2, toppingImg ? toppingImg.height : 0);
    finalCanvas.width = finalWidth;
    finalCanvas.height = finalHeight;
    finalCtx.drawImage(tempCanvas1, 0, 0, finalWidth, finalHeight);
    // Only draw color layer if it was created (color was provided)
    if (tempCanvas2 && uniqueColorImage.width > 1 && uniqueColorImage.height > 1) {
      finalCtx.drawImage(tempCanvas2, 0, 0, finalWidth, finalHeight);
    }
    if (toppingImg && toppingImg.width > 1 && toppingImg.height > 1) {
      finalCtx.drawImage(toppingImg, 0, 0, finalWidth, finalHeight);
    }

    console.log("Image merge completed successfully");
    return finalCanvas.toDataURL();
  } catch (error) {
    console.error("Error merging images:", error);
    // Return the shape URL as fallback
    return shapeUrl || '';
  }
};

/** Convert a remote image to a data URL so it embeds reliably in PDF/html2canvas. */
export const loadImageAsDataUrl = async (
  src: string
): Promise<string | null> => {
  if (!src?.trim()) return null;
  if (src.startsWith('data:')) return src;

  const tryDraw = (img: HTMLImageElement): string | null => {
    try {
      if (!img.naturalWidth || img.naturalWidth <= 1) return null;
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } catch {
      // Tainted canvas (CORS) — fall through to fetch
      return null;
    }
  };

  const loadViaImage = (url: string, useCors: boolean) =>
    new Promise<string | null>((resolve) => {
      const img = new Image();
      if (useCors) {
        img.crossOrigin = 'anonymous';
      }
      img.onload = () => resolve(tryDraw(img));
      img.onerror = () => resolve(null);
      img.src = url;
    });

  const loadViaFetch = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url, {
        credentials: 'include',
        mode: 'cors',
      });
      if (!response.ok) return null;
      const blob = await response.blob();
      if (!blob.type.startsWith('image/') && blob.type !== 'application/octet-stream') {
        // Some proxies return octet-stream for images
        if (!blob.type || blob.type === 'application/octet-stream') {
          // continue
        } else {
          return null;
        }
      }
      return await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result;
          resolve(typeof result === 'string' ? result : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  return (
    (await loadViaImage(src, true)) ||
    (await loadViaFetch(src)) ||
    (await loadViaImage(src, false)) ||
    null
  );
};

/** Try to reuse an image already rendered on the page (e.g. order details table). */
export const loadImageAsDataUrlFromDom = (
  matchKeys: string[]
): string | null => {
  const keys = matchKeys.map((k) => String(k || '').trim()).filter(Boolean);
  if (!keys.length || typeof document === 'undefined') return null;

  const imgs = Array.from(document.images || []);
  for (const img of imgs) {
    const src = img.currentSrc || img.src || '';
    if (!src || !img.complete || img.naturalWidth <= 1) continue;
    const matched = keys.some(
      (key) =>
        src === key ||
        src.includes(key) ||
        decodeURIComponent(src).includes(key)
    );
    if (!matched) continue;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    } catch {
      // Ignore tainted DOM images
    }
  }
  return null;
};
